import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { forumAPI } from '../../services/api';
import './ForumCreateForm.css';

const ForumCreateForm = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    attachments: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...files]
      }));
    }
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);

      formData.attachments.forEach((file) => {
        formDataToSend.append('attachments[]', file);
      });

      console.log('Sending forum data:', {
        title: formData.title,
        description: formData.description,
        attachments: formData.attachments.length
      });

      // Create the forum post
      const response = await forumAPI.createForum(formDataToSend);

      if (response.success) {
        onSuccess();
      } else {
        setError(response.message || 'Failed to create forum post');
      }
    } catch (err) {
      console.error('Error creating forum post:', err);
      if (err.data?.message) {
        setError(err.data.message);
      } else if (err.data?.errors) {
        // Handle validation errors
        const errorMessages = Object.values(err.data.errors).flat();
        setError(errorMessages.join(', '));
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to create forum post. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="forum-create-overlay">
      <div className="forum-create-modal">
        <div className="forum-create-header">
          <h2>Ask a Question</h2>
          <button
            className="close-btn"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="forum-create-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="title">Title (Optional)</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Brief title for your question"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Question Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe your question in detail..."
              rows="6"
              required
              className="form-textarea"
            />
            <small className="form-help">
              Minimum 10 characters. Be as specific as possible to get better help.
            </small>
          </div>

          <div className="form-group">
            <label>Attachments (Optional)</label>

            <div
              className={`file-drop-zone ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
            >
              {formData.attachments.length > 0 ? (
                // Modern UI preview for selected files
                <div className="attachments-grid">
                  {formData.attachments.map((file, index) => (
                    <div key={index} className="screenshot-content">
                      <div className="screenshot-preview">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />

                        {/* Overlay with actions */}
                        <div className="screenshot-overlay">
                          <div className="overlay-actions">
                            <label
                              htmlFor={`replace-file-${index}`}
                              className="overlay-btn replace-btn"
                              title="Replace image"
                            >
                              <i className="bi bi-arrow-clockwise"></i>
                            </label>
                            <input
                              id={`replace-file-${index}`}
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const newFile = e.target.files[0];
                                if (newFile) {
                                  setFormData((prev) => {
                                    const updated = [...prev.attachments];
                                    updated[index] = newFile;
                                    return { ...prev, attachments: updated };
                                  });
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className="overlay-btn remove-btn"
                              title="Remove this image"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="screenshot-info">
                        <div className="screenshot-name" title={file.name}>
                          {file.name.length > 15
                            ? file.name.substring(0, 15) + '...'
                            : file.name}
                        </div>
                        <div className="screenshot-size">{formatFileSize(file.size)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Empty upload box (modern look)
                <div className="upload-box">
                  <div className="upload-content">
                    <div className="upload-icon">
                      <i className="bi bi-cloud-upload"></i>
                    </div>
                    <div className="upload-text">
                      <h6>Add Attachments</h6>
                      <p>Click to upload or drag & drop</p>
                      <small>
                        Supported: JPEG, PNG, JPG, WEBP — Max 2MB each, up to 5 files
                      </small>
                    </div>
                  </div>
                </div>
              )}

              {/* Hidden input field */}
              <input
                id="file-input"
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileChange}
                className="file-input"
                style={{ display: 'none' }}
              />
            </div>
          </div>


          {formData.attachments.length > 0 && (
            <div className="attachments-preview">
              <h4>Selected Files:</h4>
              <div className="attachment-list">
                {formData.attachments.map((file, index) => (
                  <div key={index} className="attachment-item">
                    <div className="attachment-info">
                      <span className="attachment-name">{file.name}</span>
                      <span className="attachment-size">{formatFileSize(file.size)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="remove-attachment"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !formData.description.trim()}
            >
              {loading ? 'Posting...' : 'Post Question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForumCreateForm;